import React, { useEffect, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import Heatmap from 'highcharts/modules/heatmap';
import HighchartsMore from 'highcharts/highcharts-more';
import HighchartsStock from 'highcharts/modules/stock';
import { normalizePlotConfig } from 'utils/chat';
import loadingGif from 'images/loader2.webp';

Heatmap(Highcharts);
HighchartsMore(Highcharts);
HighchartsStock(Highcharts);

const BhimaHighcharts = ({ chartTypeOuter, chartTitle, data, dataToPlot }) => {
    const chartRef = useRef(null);
    const [chartOptions, setChartOptions] = useState(null);

    const { chartHeading, chartType, xAxisKey, yAxisKeys } = normalizePlotConfig(
        dataToPlot,
        Array.isArray(data) ? data : [],
        chartTitle || 'Report',
        chartTypeOuter || 'line'
    );

    useEffect(() => {
        if (!Array.isArray(data) || data.length === 0) {
            setChartOptions(null);
            return;
        }

        // Build x-axis categories
        const xValues = data.map(row => row?.[xAxisKey]);

        // Build numeric series only for y-keys
        const seriesData = [];
        for (const key of (Array.isArray(yAxisKeys) ? yAxisKeys : [])) {
            const series = data.map(row => {
                const val = row?.[key];
                return typeof val === 'number' ? val : null;
            });
            seriesData.push({ name: key, data: series });
        }

        if (seriesData.length === 0) {
            console.error('Series data is empty or undefined.');
            return;
        }

        // --- THEME STYLING START ---
        const themeColor = '#3e2b26'; // Dark Brown
        const fontTitle = 'Cinzel, serif';
        const fontBody = 'Playfair Display, serif';
        
        // Bhima/Vintage Palette
        // Gold, Rose Gold, Deep Rose, Teal, Muted Blue, Deep Brown
        const colors = [
            '#d4af37', // Metallic Gold
            '#c5a089', // Rose Gold
            '#a65e5e', // Deep Rose
            '#4a7c78', // Vintage Teal
            '#5d7a96', // Muted Blue
            '#8c6a3e', // Bronze
            '#5c442d', // Dark Wood
            '#9e857d'  // Muted Mauve
        ];

        // Determine if yAxis is an array (multiple axes) or an object (single axis)
        const yAxisConfig = Array.isArray(yAxisKeys) && yAxisKeys.length > 1
            ? yAxisKeys.map(axis => ({
                title: {
                    text: axis || 'Values',
                    style: { color: themeColor, fontFamily: fontTitle, fontWeight: 'bold' }
                },
                labels: {
                    style: { color: themeColor, fontFamily: fontBody },
                    formatter: function () {
                        return formatNumber(this.value);
                    }
                },
                gridLineColor: 'rgba(158, 133, 125, 0.2)',
                gridLineWidth: 1,
            }))
            : {
                title: {
                    text: (yAxisKeys && yAxisKeys[0]) || 'Values',
                    style: { color: themeColor, fontFamily: fontTitle, fontWeight: 'bold' }
                },
                labels: {
                    style: { color: themeColor, fontFamily: fontBody },
                    formatter: function () {
                        return formatNumber(this.value);
                    }
                },
                gridLineColor: 'rgba(158, 133, 125, 0.2)',
                gridLineWidth: 1,
            };

        const firstYKey = Array.isArray(yAxisKeys) ? yAxisKeys[0] : undefined;
        const requestedType = chartTypeOuter || chartType;
        const normalizedType = String(requestedType || '').toLowerCase() === 'donut' ? 'pie' : requestedType;
        const isDonut = String(requestedType || '').toLowerCase() === 'donut';
        const pieSeries = (chartType === 'pie' && firstYKey)
            ? [{
                name: firstYKey || 'Values',
                data: data.map(row => {
                    const name = row?.[xAxisKey];
                    const val = row?.[firstYKey];
                    if (typeof val !== 'number') return null;
                    return { name: String(name ?? ''), y: val };
                }).filter(Boolean)
            }]
            : seriesData;

        const options = {
            colors: colors,
            chart: {
                backgroundColor: 'transparent',
                plotBackgroundColor: 'transparent',
                type: String(normalizedType || '').toLowerCase() === 'pie' ? 'pie' : normalizedType,
                height: 350,
                style: {
                    fontFamily: fontBody
                }
            },
            title: {
                text: chartHeading,
                style: { color: themeColor, fontFamily: fontTitle, fontSize: '18px', fontWeight: 'bold' }
            },
            xAxis: {
                categories: xValues,
                title: {
                    text: xAxisKey,
                    style: { color: themeColor, fontFamily: fontTitle, fontWeight: 'bold' }
                },
                labels: {
                    style: { color: themeColor, fontFamily: fontBody }
                },
                lineColor: '#9e857d',
                tickColor: '#9e857d',
                gridLineWidth: 0,
                min: 0,
                max: xValues.length > 10 ? 9 : undefined,
                scrollbar: {
                    enabled: xValues.length > 10,
                    barBackgroundColor: '#c5a089',
                    barBorderRadius: 4,
                    barBorderWidth: 0,
                    buttonBackgroundColor: '#c5a089',
                    buttonBorderWidth: 0,
                    buttonArrowColor: '#3e2b26',
                    buttonBorderRadius: 4,
                    rifleColor: '#3e2b26',
                    trackBackgroundColor: 'rgba(197, 160, 137, 0.1)',
                    trackBorderWidth: 1,
                    trackBorderColor: '#c5a089',
                    trackBorderRadius: 4
                }
            },
            yAxis: yAxisConfig,
            series: pieSeries,
            credits: { enabled: false },
            legend: {
                itemStyle: { color: themeColor, fontFamily: fontBody, fontSize: '12px' },
                itemHoverStyle: { color: '#000000' }
            },
            plotOptions: {
                series: {
                    dataLabels: {
                        enabled: true,
                        color: themeColor,
                        style: { fontFamily: fontBody, textOutline: 'none' },
                        formatter: function () {
                            return formatNumber(this.y);
                        }
                    },
                    connectNulls: true,
                    marker: { enabled: true }
                },
                pie: {
                    borderColor: '#ebd8cf',
                    borderWidth: 2,
                    innerSize: isDonut ? '60%' : '0%',
                    dataLabels: {
                        enabled: true,
                        color: themeColor,
                        style: { fontFamily: fontBody, textOutline: 'none' },
                        connectorColor: '#9e857d',
                        formatter: function () {
                            return `<b>${this.point.name}</b>: ${formatNumber(this.y)}`;
                        }
                    }
                }
            },
            tooltip: {
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderColor: '#9e857d',
                borderRadius: 2,
                style: { color: themeColor, fontFamily: fontBody },
                formatter: function () {
                    return `<b>${this.series.name}</b><br/>${this.key}: ${formatNumber(this.y)}`;
                }
            }
        };

        setChartOptions(options);
    }, [chartType, data, chartHeading, xAxisKey, JSON.stringify(yAxisKeys)]);

    const formatNumber = (value) => {
        if (typeof value === 'number') {
            if (value >= 1e9) {
                return '₹' + (value / 1e9).toFixed(1) + 'B'; // Or Cr? User asked for Cr specifically for stats, not sure about all charts. Defaulting to standard short format for now.
            } else if (value >= 1e7) { // Crore logic if we want consistent Indian numbering
                 return '₹' + (value / 1e7).toFixed(2) + 'Cr';
            } else if (value >= 1e5) {
                 return '₹' + (value / 1e5).toFixed(2) + 'L';
            } else if (value >= 1e3) {
                return (value / 1e3).toFixed(1) + 'k';
            } else {
                return value.toFixed(2);
            }
        }
        return value; // Return as is if not a number
    };

    if (!chartOptions) {
        const isEmpty = !Array.isArray(data) || data.length === 0;
        return (
            <div className="flex flex-col items-center justify-center my-2 gap-2 min-h-[350px]">
                {isEmpty ? (
                    <span style={{ fontFamily: 'Cinzel', color: '#3e2b26' }}>No data available</span>
                ) : (
                    <>
                        <div>
                            <img src={loadingGif} alt="loading" style={{ height: '24px' }} />
                        </div>
                        <span style={{ fontFamily: 'Cinzel', color: '#3e2b26' }}>Generating Chart...</span>
                    </>
                )}
            </div>
        );
    }

    return <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} />;
};

export default BhimaHighcharts;
